// src/BookingManagement/bikeUtils.js - COMPLETE MULTI-TENANT VERSION
import { getRentals, getRentalsByBike } from '../services/firebaseInvoiceService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// ===== MULTI-TENANT FIREBASE DATA LOADING AND CACHING =====

// Company-specific cache for rentals data
const companyRentalCaches = new Map();
const companyManagedBikeCaches = new Map();

/**
 * Get company-specific cache key
 * @param {string} companyId - Company ID
 * @param {string} type - Cache type ('rentals' or 'managedBikes')
 * @returns {string} - Cache key
 */
const getCompanyCacheKey = (companyId, type) => {
  return `${companyId || 'default'}_${type}`;
};

/**
 * Load rentals from Firebase with company-specific caching
 * @param {string} companyId - Company ID for data isolation (REQUIRED)
 * @param {boolean} forceRefresh - Force refresh from Firebase
 * @returns {Promise<Array>} - Promise resolving to company-specific rentals array
 */
export const loadRentalsFromFirebase = async (companyId, forceRefresh = false) => {
  try {
    if (!companyId) {
      console.error('❌ loadRentalsFromFirebase: Company ID required for loading rentals');
      return [];
    }

    const cacheKey = getCompanyCacheKey(companyId, 'rentals');
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    
    // Check company-specific cache
    const cachedData = companyRentalCaches.get(cacheKey);
    if (!forceRefresh && cachedData && (now - cachedData.timestamp) < CACHE_DURATION) {
      console.log(`🏢 bikeUtils: Returning cached rentals for company: ${companyId}`);
      return cachedData.data;
    }
    
    console.log(`🏢 bikeUtils: Loading rentals from Firebase for company: ${companyId}...`);
    const rentals = await getRentals(companyId);
    
    // Ensure all rentals have companyId
    const rentalsWithCompany = rentals.map(rental => ({
      ...rental,
      companyId: companyId,
      _tenantIsolated: true
    }));
    
    // Update company-specific cache
    companyRentalCaches.set(cacheKey, {
      data: rentalsWithCompany,
      timestamp: now,
      companyId: companyId
    });
    
    console.log(`📊 bikeUtils: Loaded ${rentalsWithCompany.length} rentals for company: ${companyId}`);
    return rentalsWithCompany;
  } catch (error) {
    console.error(`❌ Error loading rentals for company ${companyId}:`, error);
    
    // Return cached data if available, otherwise empty array
    const cacheKey = getCompanyCacheKey(companyId, 'rentals');
    const cachedData = companyRentalCaches.get(cacheKey);
    return cachedData ? cachedData.data : [];
  }
};

/**
 * Load managed bikes from Firebase with company-specific caching
 * @param {string} companyId - Company ID for data isolation (REQUIRED)
 * @param {boolean} forceRefresh - Force refresh from Firebase
 * @returns {Promise<Array>} - Promise resolving to company-specific managed bikes array
 */
export const loadManagedBikesFromFirebase = async (companyId, forceRefresh = false) => {
  try {
    if (!companyId) {
      console.error('❌ loadManagedBikesFromFirebase: Company ID required for loading managed bikes');
      return [];
    }

    const cacheKey = getCompanyCacheKey(companyId, 'managedBikes');
    const now = Date.now();
    const MANAGED_BIKES_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
    
    // Check company-specific cache
    const cachedData = companyManagedBikeCaches.get(cacheKey);
    if (!forceRefresh && cachedData && (now - cachedData.timestamp) < MANAGED_BIKES_CACHE_DURATION) {
      console.log(`🏢 bikeUtils: Returning cached managed bikes for company: ${companyId}`);
      return cachedData.data;
    }
    
    console.log(`🏢 bikeUtils: Loading managed bikes from Firebase for company: ${companyId}...`);
    const managedBikesCollection = collection(db, 'managedBikes');
    
    // Company-specific query
    const q = query(managedBikesCollection, where('companyId', '==', companyId));
    const querySnapshot = await getDocs(q);
    const managedBikes = [];
    
    querySnapshot.forEach((doc) => {
      const bikeData = {
        id: doc.id,
        ...doc.data(),
        companyId: companyId, // Ensure company ID
        _tenantIsolated: true
      };
      managedBikes.push(bikeData);
    });
    
    // Update company-specific cache
    companyManagedBikeCaches.set(cacheKey, {
      data: managedBikes,
      timestamp: now,
      companyId: companyId
    });
    
    console.log(`📊 bikeUtils: Loaded ${managedBikes.length} managed bikes for company: ${companyId}`);
    return managedBikes;
  } catch (error) {
    console.error(`❌ Error loading managed bikes for company ${companyId}:`, error);
    
    // Return cached data if available, otherwise empty array
    const cacheKey = getCompanyCacheKey(companyId, 'managedBikes');
    const cachedData = companyManagedBikeCaches.get(cacheKey);
    return cachedData ? cachedData.data : [];
  }
};

/**
 * Update cached rentals data for specific company
 * @param {Array} newRentals - New rentals array
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Array} - Updated rentals array
 */
export const updateCachedRentals = (newRentals, companyId) => {
  if (!companyId) {
    console.error('❌ updateCachedRentals: Company ID required for updating cached rentals');
    return [];
  }

  if (Array.isArray(newRentals)) {
    const cacheKey = getCompanyCacheKey(companyId, 'rentals');
    const rentalsWithCompany = newRentals.map(rental => ({
      ...rental,
      companyId: companyId,
      _tenantIsolated: true
    }));
    
    companyRentalCaches.set(cacheKey, {
      data: rentalsWithCompany,
      timestamp: Date.now(),
      companyId: companyId
    });
    
    console.log(`💾 bikeUtils: Updated cached rentals for company ${companyId} (${rentalsWithCompany.length} items)`);
    return rentalsWithCompany;
  }
  return [];
};

/**
 * Update cached managed bikes data for specific company
 * @param {Array} newManagedBikes - New managed bikes array
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Array} - Updated managed bikes array
 */
export const updateCachedManagedBikes = (newManagedBikes, companyId) => {
  if (!companyId) {
    console.error('❌ updateCachedManagedBikes: Company ID required for updating cached managed bikes');
    return [];
  }

  if (Array.isArray(newManagedBikes)) {
    const cacheKey = getCompanyCacheKey(companyId, 'managedBikes');
    const bikesWithCompany = newManagedBikes.map(bike => ({
      ...bike,
      companyId: companyId,
      _tenantIsolated: true
    }));
    
    companyManagedBikeCaches.set(cacheKey, {
      data: bikesWithCompany,
      timestamp: Date.now(),
      companyId: companyId
    });
    
    console.log(`💾 bikeUtils: Updated cached managed bikes for company ${companyId} (${bikesWithCompany.length} items)`);
    return bikesWithCompany;
  }
  return [];
};

/**
 * Get cached rentals for specific company without Firebase call
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Array} - Cached company-specific rentals array
 */
export const getCachedRentals = (companyId) => {
  if (!companyId) {
    console.error('❌ getCachedRentals: Company ID required for getting cached rentals');
    return [];
  }

  const cacheKey = getCompanyCacheKey(companyId, 'rentals');
  const cachedData = companyRentalCaches.get(cacheKey);
  
  if (cachedData) {
    console.log(`📊 bikeUtils: Retrieved ${cachedData.data.length} cached rentals for company: ${companyId}`);
    return [...cachedData.data];
  }
  
  return [];
};

/**
 * Get cached managed bikes for specific company without Firebase call
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Array} - Cached company-specific managed bikes array
 */
export const getCachedManagedBikes = (companyId) => {
  if (!companyId) {
    console.error('❌ getCachedManagedBikes: Company ID required for getting cached managed bikes');
    return [];
  }

  const cacheKey = getCompanyCacheKey(companyId, 'managedBikes');
  const cachedData = companyManagedBikeCaches.get(cacheKey);
  
  if (cachedData) {
    console.log(`📊 bikeUtils: Retrieved ${cachedData.data.length} cached managed bikes for company: ${companyId}`);
    return [...cachedData.data];
  }
  
  return [];
};

/**
 * Clear cached rentals data for specific company
 * @param {string} companyId - Company ID (optional - if not provided, clears all)
 */
export const clearCachedRentals = (companyId) => {
  if (!companyId) {
    console.warn('⚠️ No company ID provided for clearing cache, clearing all rental caches');
    companyRentalCaches.clear();
  } else {
    const cacheKey = getCompanyCacheKey(companyId, 'rentals');
    companyRentalCaches.delete(cacheKey);
    console.log(`🧹 bikeUtils: Cleared cached rentals for company: ${companyId}`);
  }
};

/**
 * Clear cached managed bikes data for specific company
 * @param {string} companyId - Company ID (optional - if not provided, clears all)
 */
export const clearCachedManagedBikes = (companyId) => {
  if (!companyId) {
    console.warn('⚠️ No company ID provided for clearing cache, clearing all managed bike caches');
    companyManagedBikeCaches.clear();
  } else {
    const cacheKey = getCompanyCacheKey(companyId, 'managedBikes');
    companyManagedBikeCaches.delete(cacheKey);
    console.log(`🧹 bikeUtils: Cleared cached managed bikes for company: ${companyId}`);
  }
};

/**
 * Initialize bike management system for specific company
 * @param {string} companyId - Company ID (REQUIRED)
 * @param {Object} options - Initialization options
 * @returns {Promise<boolean>} - Success status
 */
export const initializeBikeManagement = async (companyId, options = {}) => {
  try {
    if (!companyId) {
      console.error('❌ initializeBikeManagement: Company ID required for initializing bike management');
      return false;
    }

    console.log(`🏢 bikeUtils: Initializing bike management system for company: ${companyId}...`);
    
    // Load initial company-specific data
    await Promise.all([
      loadRentalsFromFirebase(companyId, true),
      loadManagedBikesFromFirebase(companyId, true)
    ]);
    
    // Set up auto-refresh for this company if requested
    if (options.autoRefresh) {
      const refreshInterval = options.refreshInterval || 300000; // Default 5 minutes
      
      setInterval(() => {
        console.log(`🔄 Auto-refreshing data for company: ${companyId}`);
        loadRentalsFromFirebase(companyId, true);
        loadManagedBikesFromFirebase(companyId, true);
      }, refreshInterval);
    }
    
    console.log(`✅ bikeUtils: Bike management system initialized successfully for company: ${companyId}`);
    return true;
  } catch (error) {
    console.error(`❌ Error initializing bike management for company ${companyId}:`, error);
    return false;
  }
};

// ===== DATE UTILITY FUNCTIONS =====

/**
 * Check if a date string represents today's date
 * @param {string} dateString - Date string to check
 * @returns {boolean} - True if the date is today
 */
export const isToday = (dateString) => {
  if (!dateString) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const checkDate = new Date(dateString);
  checkDate.setHours(0, 0, 0, 0);
  
  return today.getTime() === checkDate.getTime();
};

/**
 * Check if a date string represents tomorrow's date
 * @param {string} dateString - Date string to check
 * @returns {boolean} - True if the date is tomorrow
 */
export const isTomorrow = (dateString) => {
  if (!dateString) return false;
  
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const checkDate = new Date(dateString);
  checkDate.setHours(0, 0, 0, 0);
  
  return tomorrow.getTime() === checkDate.getTime();
};

/**
 * Calculate rental duration in days
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @returns {number} - Duration in days
 */
export const calculateRentalDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Get the number of days between two dates
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @returns {number} - Days difference (positive number)
 */
export const getDaysBetween = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  
  const diffTime = Math.abs(d2 - d1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// ===== BIKE MODEL AND PRICING FUNCTIONS =====

/**
 * Get the daily rate for a bike model (fallback to hardcoded rates)
 * @param {string} model - Bike model name
 * @returns {number} - Daily rate in dollars
 */
export const getModelRateFallback = (model) => {
  const rates = {
    'DIO': 25,
    'APRILIA': 30,
    'XPULSE': 35,
    'XBLADE': 30,
    'HORNET': 35,
    'SHINE': 25,
    'NAVI': 20,
    'TRIGGER': 25,
    'NTORQ': 25,
    'HONDA CD': 25,
    'XR': 30,
    'DEFAULT': 25
  };
  
  return rates[model?.toUpperCase()] || rates['DEFAULT'];
};

/**
 * Get the daily rate for a bike model (synchronous version with company context)
 * @param {string} model - Bike model name
 * @param {string} companyId - Company ID (REQUIRED for managed bikes)
 * @returns {number} - Daily rate in dollars
 */
export const getModelRate = (model, companyId = null) => {
  // Check if we have cached managed bikes data for the company
  if (companyId) {
    const managedBikes = getCachedManagedBikes(companyId);
    
    if (managedBikes.length > 0) {
      const matchingBikes = managedBikes.filter(bike => 
        bike.model && bike.model.toUpperCase() === model?.toUpperCase()
      );
      
      if (matchingBikes.length > 0 && matchingBikes[0].dailyRate) {
        console.log(`💰 Using managed bike rate for ${model} (Company: ${companyId}): $${matchingBikes[0].dailyRate}`);
        return matchingBikes[0].dailyRate;
      }
    }
  }
  
  // Fall back to hardcoded rates
  const fallbackRate = getModelRateFallback(model);
  if (companyId) {
    console.log(`💰 Using fallback rate for ${model} (Company: ${companyId}): $${fallbackRate}`);
  }
  return fallbackRate;
};

/**
 * Get the daily rate for a bike model from managed bikes data
 * @param {string} model - Bike model name
 * @param {string} bikeId - Specific bike ID (optional)
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Promise<number>} - Daily rate in dollars
 */
export const getModelRateFromManaged = async (model, bikeId = null, companyId) => {
  try {
    if (!companyId) {
      console.error('❌ getModelRateFromManaged: Company ID required');
      return getModelRateFallback(model);
    }

    const cachedBikes = getCachedManagedBikes(companyId);
    let managedBikes = cachedBikes;
    
    if (managedBikes.length === 0) {
      managedBikes = await loadManagedBikesFromFirebase(companyId);
    }
    
    // If we have a specific bike ID, find that exact bike
    if (bikeId) {
      const specificBike = managedBikes.find(bike => 
        bike.regNumber === bikeId || 
        bike.id === bikeId ||
        `${bike.model} - ${bike.regNumber}` === bikeId
      );
      
      if (specificBike && specificBike.dailyRate) {
        console.log(`💰 Found managed rate for specific bike ${bikeId} (Company: ${companyId}): $${specificBike.dailyRate}`);
        return specificBike.dailyRate;
      }
    }
    
    // Find bikes with matching model
    const matchingBikes = managedBikes.filter(bike => 
      bike.model && bike.model.toUpperCase() === model?.toUpperCase()
    );
    
    if (matchingBikes.length > 0) {
      const bike = matchingBikes[0];
      if (bike.dailyRate) {
        console.log(`💰 Found managed rate for model ${model} (Company: ${companyId}): $${bike.dailyRate}`);
        return bike.dailyRate;
      }
    }
    
    console.log(`💰 No managed bike found for model ${model} (Company: ${companyId}), using fallback rate`);
    return getModelRateFallback(model);
    
  } catch (error) {
    console.error(`❌ Error getting managed bike rate for company ${companyId}:`, error);
    return getModelRateFallback(model);
  }
};

/**
 * Get bike details including rate from managed bikes
 * @param {string} bikeId - Bike identifier (registration number or ID)
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Promise<Object>} - Bike details object
 */
export const getManagedBikeDetails = async (bikeId, companyId) => {
  try {
    if (!companyId) {
      console.error('❌ getManagedBikeDetails: Company ID required');
      return {
        id: bikeId,
        regNumber: bikeId,
        model: 'Unknown',
        dailyRate: getModelRateFallback('DEFAULT'),
        status: 'unknown',
        isManaged: false,
        companyId: null
      };
    }

    let managedBikes = getCachedManagedBikes(companyId);
    if (managedBikes.length === 0) {
      managedBikes = await loadManagedBikesFromFirebase(companyId);
    }
    
    const bike = managedBikes.find(bike => 
      bike.regNumber === bikeId || 
      bike.id === bikeId ||
      `${bike.model} - ${bike.regNumber}` === bikeId
    );
    
    if (bike) {
      console.log(`🏍️ Found managed bike details for ${bikeId} (Company: ${companyId}):`, bike);
      return {
        id: bike.id,
        regNumber: bike.regNumber,
        model: bike.model,
        dailyRate: bike.dailyRate || getModelRateFallback(bike.model),
        status: bike.status || 'available',
        color: bike.color,
        year: bike.year,
        condition: bike.condition,
        isManaged: true,
        companyId: bike.companyId
      };
    }
    
    console.log(`🏍️ No managed bike found for ${bikeId} (Company: ${companyId}), using fallback`);
    return {
      id: bikeId,
      regNumber: bikeId,
      model: 'Unknown',
      dailyRate: getModelRateFallback('DEFAULT'),
      status: 'unknown',
      isManaged: false,
      companyId: companyId
    };
    
  } catch (error) {
    console.error(`❌ Error getting managed bike details for company ${companyId}:`, error);
    return {
      id: bikeId,
      regNumber: bikeId,
      model: 'Unknown',
      dailyRate: getModelRateFallback('DEFAULT'),
      status: 'unknown',
      isManaged: false,
      companyId: companyId
    };
  }
};

/**
 * Calculate overdue cost based on bike model and days overdue
 * @param {string} bikeModel - Bike model or full bike string
 * @param {number} daysOverdue - Number of days overdue
 * @param {string} companyId - Company ID (optional for managed bikes)
 * @returns {number} - Total overdue cost
 */
export const calculateOverdueCost = (bikeModel, daysOverdue, companyId = null) => {
  if (!bikeModel || daysOverdue <= 0) return 0;
  
  let model = bikeModel;
  if (bikeModel.includes(' - ')) {
    model = bikeModel.split(' - ')[0];
  }
  
  const dailyRate = getModelRate(model, companyId);
  const cost = dailyRate * daysOverdue;
  
  if (companyId) {
    console.log(`💰 Calculated overdue cost for ${model} (Company: ${companyId}): ${daysOverdue} days × $${dailyRate} = $${cost}`);
  }
  
  return cost;
};

/**
 * Calculate overdue cost using managed bike data
 * @param {string} bikeId - Bike identifier
 * @param {number} daysOverdue - Number of days overdue
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Promise<number>} - Total overdue cost
 */
export const calculateOverdueCostFromManaged = async (bikeId, daysOverdue, companyId) => {
  if (!bikeId || daysOverdue <= 0) return 0;
  
  if (!companyId) {
    console.error('❌ calculateOverdueCostFromManaged: Company ID required');
    return calculateOverdueCost(bikeId, daysOverdue);
  }
  
  try {
    const bikeDetails = await getManagedBikeDetails(bikeId, companyId);
    const cost = bikeDetails.dailyRate * daysOverdue;
    console.log(`💰 Calculated managed overdue cost for ${bikeId} (Company: ${companyId}): ${daysOverdue} days × $${bikeDetails.dailyRate} = $${cost}`);
    return cost;
  } catch (error) {
    console.error(`❌ Error calculating overdue cost from managed bike for company ${companyId}:`, error);
    return calculateOverdueCost(bikeId, daysOverdue, companyId);
  }
};

/**
 * Calculate total rental cost
 * @param {string} bikeModel - Bike model
 * @param {number} days - Number of days
 * @param {number} pickupFee - Pickup delivery fee (optional)
 * @param {number} dropoffFee - Dropoff delivery fee (optional)
 * @param {string} companyId - Company ID (optional for managed bikes)
 * @returns {number} - Total cost
 */
export const calculateTotalCost = (bikeModel, days, pickupFee = 0, dropoffFee = 0, companyId = null) => {
  if (!bikeModel || days <= 0) return pickupFee + dropoffFee;
  
  const dailyRate = getModelRate(bikeModel, companyId);
  const rentalCost = dailyRate * days;
  const totalCost = rentalCost + pickupFee + dropoffFee;
  
  if (companyId) {
    console.log(`💰 Calculated total cost for ${bikeModel} (Company: ${companyId}): ${days} days × $${dailyRate} + $${pickupFee} pickup + $${dropoffFee} dropoff = $${totalCost}`);
  }
  
  return totalCost;
};

/**
 * Calculate total rental cost using managed bike data
 * @param {string} bikeId - Bike identifier
 * @param {number} days - Number of days
 * @param {number} pickupFee - Pickup delivery fee (optional)
 * @param {number} dropoffFee - Dropoff delivery fee (optional)
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Promise<number>} - Total cost
 */
export const calculateTotalCostFromManaged = async (bikeId, days, pickupFee = 0, dropoffFee = 0, companyId) => {
  if (!companyId) {
    console.error('❌ calculateTotalCostFromManaged: Company ID required');
    return calculateTotalCost(bikeId, days, pickupFee, dropoffFee);
  }

  if (!bikeId || days <= 0) return pickupFee + dropoffFee;

  try {
    const bikeDetails = await getManagedBikeDetails(bikeId, companyId);
    const rentalCost = bikeDetails.dailyRate * days;
    const totalCost = rentalCost + pickupFee + dropoffFee;
    
    console.log(`💰 Calculated managed total cost for ${bikeId} (Company: ${companyId}): ${days} days × $${bikeDetails.dailyRate} + $${pickupFee} pickup + $${dropoffFee} dropoff = $${totalCost}`);
    return totalCost;
  } catch (error) {
    console.error(`❌ Error calculating total cost from managed bike for company ${companyId}:`, error);
    return calculateTotalCost(bikeId, days, pickupFee, dropoffFee, companyId);
  }
};

// ===== RENTAL STATUS FUNCTIONS =====

/**
 * Check if a rental is overdue and calculate days overdue
 * @param {Object} rental - Rental object
 * @returns {Object} - {isOverdue: boolean, daysOverdue: number}
 */
export const checkOverdueStatus = (rental) => {
  if (!rental || !rental.endDate) {
    return { isOverdue: false, daysOverdue: 0 };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const endDate = new Date(rental.endDate);
  endDate.setHours(0, 0, 0, 0);
  
  if (today > endDate) {
    const diffTime = today - endDate;
    const daysOverdue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return { isOverdue: true, daysOverdue };
  }
  
  return { isOverdue: false, daysOverdue: 0 };
};

/**
 * Calculate dynamic rental status based on current date
 * @param {Object} rental - Rental object
 * @returns {string} - Status (Reserved, Active, Due Today, Overdue, Returned)
 */
export const calculateDynamicStatus = (rental) => {
  if (!rental || !rental.startDate || !rental.endDate) {
    return rental?.status || "Unknown";
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = new Date(rental.startDate);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(rental.endDate);
  endDate.setHours(0, 0, 0, 0);

  if (rental.status === "Returned") {
    return "Returned";
  }

  if (today < startDate) {
    return "Reserved";
  } else if (today.getTime() === startDate.getTime()) {
    return rental.status === "Active" ? "Active" : "Reserved";
  } else if (today > startDate && today < endDate) {
    return "Active";
  } else if (today.getTime() === endDate.getTime()) {
    return "Due Today";
  } else if (today > endDate) {
    return "Overdue";
  }

  return rental.status || "Unknown";
};

// ===== MULTI-TENANT FILTERING FUNCTIONS =====

/**
 * Get today's pickups from rentals array with company isolation
 * @param {Array} rentals - Array of rental objects
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Array} - Array of company-specific rentals scheduled for pickup today
 */
export const getTodaysPickups = (rentals, companyId) => {
  if (!Array.isArray(rentals)) {
    console.log('❌ getTodaysPickups: Invalid rentals array');
    return [];
  }

  if (!companyId) {
    console.error('❌ getTodaysPickups: Company ID required for multi-tenant operation');
    return [];
  }
  
  const companyRentals = rentals.filter(rental => rental.companyId === companyId);
  
  console.log(`🏢 getTodaysPickups: Processing ${companyRentals.length} rentals for company: ${companyId}`);
  
  const todaysPickups = companyRentals.filter(rental => {
    if (!rental.startDate) return false;
    if (!isToday(rental.startDate)) return false;
    if (rental.status === 'Returned') return false;
    return true;
  });
  
  console.log(`📊 getTodaysPickups: Found ${todaysPickups.length} today's pickups for company: ${companyId}`);
  return todaysPickups;
};

/**
 * Get today's returns from rentals array with company isolation
 * @param {Array} rentals - Array of rental objects
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Array} - Array of company-specific rentals due for return today
 */
export const getTodaysReturns = (rentals, companyId) => {
  if (!Array.isArray(rentals)) {
    console.log('❌ getTodaysReturns: Invalid rentals array');
    return [];
  }

  if (!companyId) {
    console.error('❌ getTodaysReturns: Company ID required for multi-tenant operation');
    return [];
  }
  
  const companyRentals = rentals.filter(rental => rental.companyId === companyId);
  
  return companyRentals.filter(rental => {
    return rental.endDate && 
           isToday(rental.endDate) && 
           rental.status !== 'Returned';
  });
};

/**
 * Get rentals due today (alias for getTodaysReturns for compatibility)
 * @param {Array} rentals - Array of rental objects
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Array} - Array of company-specific rentals due for return today
 */
export const getRentalsDueToday = (rentals, companyId) => {
  return getTodaysReturns(rentals, companyId);
};

/**
 * Get overdue rentals from rentals array with company isolation
 * @param {Array} rentals - Array of rental objects
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Array} - Array of company-specific overdue rentals
 */
export const getOverdueRentals = (rentals, companyId) => {
  if (!Array.isArray(rentals)) {
    console.log('❌ getOverdueRentals: Invalid rentals array');
    return [];
  }

  if (!companyId) {
    console.error('❌ getOverdueRentals: Company ID required for multi-tenant operation');
    return [];
  }
  
  const companyRentals = rentals.filter(rental => rental.companyId === companyId);
  
  return companyRentals.filter(rental => {
    if (!rental.endDate || rental.status === 'Returned') return false;
    
    const { isOverdue } = checkOverdueStatus(rental);
    return isOverdue;
  });
};

/**
 * Get active rentals for specific company
 * @param {Array} rentals - Array of rental objects
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Array} - Array of company-specific active rentals
 */
export const getActiveRentals = (rentals, companyId) => {
  if (!Array.isArray(rentals)) {
    console.log('❌ getActiveRentals: Invalid rentals array');
    return [];
  }

  if (!companyId) {
    console.error('❌ getActiveRentals: Company ID required for multi-tenant operation');
    return [];
  }
  
  return rentals.filter(rental => 
    rental.companyId === companyId && rental.status === 'Active'
  );
};

/**
 * Get reserved rentals for specific company
 * @param {Array} rentals - Array of rental objects
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Array} - Array of company-specific reserved rentals
 */
export const getReservedRentals = (rentals, companyId) => {
  if (!Array.isArray(rentals)) {
    console.log('❌ getReservedRentals: Invalid rentals array');
    return [];
  }

  if (!companyId) {
    console.error('❌ getReservedRentals: Company ID required for multi-tenant operation');
    return [];
  }
  
  return rentals.filter(rental => 
    rental.companyId === companyId && rental.status === 'Reserved'
  );
};

/**
 * Get company rental statistics
 * @param {Array} rentals - Array of rental objects
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Object} - Company-specific rental statistics
 */
export const getCompanyRentalStats = (rentals, companyId) => {
  if (!Array.isArray(rentals)) {
    return {
      companyId: companyId,
      total: 0,
      active: 0,
      reserved: 0,
      overdue: 0,
      returned: 0,
      dueToday: 0,
      pickupsToday: 0
    };
  }

  if (!companyId) {
    console.error('❌ getCompanyRentalStats: Company ID required for multi-tenant operation');
    return {
      companyId: companyId,
      total: 0,
      active: 0,
      reserved: 0,
      overdue: 0,
      returned: 0,
      dueToday: 0,
      pickupsToday: 0
    };
  }
  
  const companyRentals = rentals.filter(rental => rental.companyId === companyId);
  
  return {
    companyId: companyId,
    total: companyRentals.length,
    active: companyRentals.filter(r => r.status === 'Active').length,
    reserved: companyRentals.filter(r => r.status === 'Reserved').length,
    overdue: getOverdueRentals(rentals, companyId).length,
    returned: companyRentals.filter(r => r.status === 'Returned').length,
    dueToday: getTodaysReturns(rentals, companyId).length,
    pickupsToday: getTodaysPickups(rentals, companyId).length
  };
};

// ===== MULTI-TENANT BIKE AVAILABILITY FUNCTIONS =====

/**
 * Check if a bike is available for given dates with company isolation
 * @param {string} bikeId - Bike identifier
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {string} companyId - Company ID for isolation (REQUIRED)
 * @returns {Promise<boolean>} - Promise resolving to availability status
 */
export const isBikeAvailable = async (bikeId, startDate, endDate, companyId) => {
  try {
    if (!companyId) {
      console.error('❌ isBikeAvailable: Company ID required for checking bike availability');
      return false;
    }

    const rentals = await getRentalsByBike(bikeId, companyId);
    return isBikeAvailableLocal(bikeId, startDate, endDate, rentals, companyId);
  } catch (error) {
    console.error(`❌ Error checking bike availability for company ${companyId}:`, error);
    return false;
  }
};

/**
 * Check if a bike is available for given dates (local check with company isolation)
 * @param {string} bikeId - Bike identifier
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {Array} existingRentals - Array of existing rentals to check against
 * @param {string} companyId - Company ID for isolation (REQUIRED)
 * @returns {boolean} - True if available
 */
export const isBikeAvailableLocal = (bikeId, startDate, endDate, existingRentals, companyId) => {
  if (!Array.isArray(existingRentals)) return true;
  
  if (!companyId) {
    console.error('❌ isBikeAvailableLocal: Company ID required for multi-tenant operation');
    return false;
  }
  
  const companyRentals = existingRentals.filter(rental => rental.companyId === companyId);
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  for (const rental of companyRentals) {
    if (rental.bike !== bikeId || rental.status === 'Returned') continue;
    
    const rentalStart = new Date(rental.startDate);
    const rentalEnd = new Date(rental.endDate);
    
    if (start <= rentalEnd && end >= rentalStart) {
      console.log(`🚫 Bike ${bikeId} not available for ${startDate} to ${endDate} (Company: ${companyId}) - conflicts with rental ${rental.id}`);
      return false;
    }
  }
  
  console.log(`✅ Bike ${bikeId} is available for ${startDate} to ${endDate} (Company: ${companyId})`);
  return true;
};

/**
 * Filter available bikes from a list with company isolation
 * @param {Array} bikes - Array of bike objects
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {string} companyId - Company ID for isolation (REQUIRED)
 * @returns {Promise<Array>} - Promise resolving to array of available bikes
 */
export const filterAvailableBikes = async (bikes, startDate, endDate, companyId) => {
  try {
    if (!startDate || !endDate || !Array.isArray(bikes)) {
      return bikes || [];
    }

    if (!companyId) {
      console.error('❌ filterAvailableBikes: Company ID required for multi-tenant operation');
      return bikes || [];
    }

    console.log(`🏢 Filtering available bikes for company ${companyId}: ${bikes.length} bikes to check`);

    const availabilityChecks = bikes.map(async (bike) => {
      const bikeName = bike.name || bike;
      const isAvailable = await isBikeAvailable(bikeName, startDate, endDate, companyId);
      return isAvailable ? bike : null;
    });

    const results = await Promise.all(availabilityChecks);
    const availableBikes = results.filter(bike => bike !== null);
    
    console.log(`✅ Found ${availableBikes.length} available bikes for company ${companyId}`);
    return availableBikes;
  } catch (error) {
    console.error(`❌ Error filtering available bikes for company ${companyId}:`, error);
    return bikes || [];
  }
};

/**
 * Filter available bikes from a list using local rental data with company isolation
 * @param {Array} bikes - Array of bike objects
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {Array} existingRentals - Array of existing rentals
 * @param {string} companyId - Company ID for isolation (REQUIRED)
 * @returns {Array} - Array of available bikes
 */
export const filterAvailableBikesLocal = (bikes, startDate, endDate, existingRentals, companyId) => {
  if (!startDate || !endDate || !Array.isArray(bikes)) {
    return bikes || [];
  }

  if (!companyId) {
    console.error('❌ filterAvailableBikesLocal: Company ID required for multi-tenant operation');
    return bikes || [];
  }

  return bikes.filter(bike => {
    const bikeName = bike.name || bike;
    return isBikeAvailableLocal(bikeName, startDate, endDate, existingRentals, companyId);
  });
};

/**
 * Get bike rental status with company isolation
 * @param {string} bikeId - Bike identifier
 * @param {string} companyId - Company ID for isolation (REQUIRED)
 * @returns {Promise<Object>} - Promise resolving to status object
 */
export const getBikeRentalStatus = async (bikeId, companyId) => {
  try {
    if (!companyId) {
      console.error('❌ getBikeRentalStatus: Company ID required for getting bike rental status');
      return { status: 'Unknown', rental: null, error: 'Company ID required' };
    }

    const rentals = await getRentalsByBike(bikeId, companyId);
    const companyRentals = rentals.filter(rental => rental.companyId === companyId);
    const activeRentals = companyRentals.filter(rental => rental.status !== 'Returned');
    
    if (activeRentals.length === 0) {
      return { status: 'Available', rental: null, companyId: companyId };
    }

    activeRentals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const currentRental = activeRentals[0];

    const calculatedStatus = calculateDynamicStatus(currentRental);

    return {
      status: calculatedStatus,
      rental: currentRental,
      customerName: currentRental.customerDetails?.name || 'Unknown',
      startDate: currentRental.startDate,
      endDate: currentRental.endDate,
      invoiceNumber: currentRental.invoiceNumber,
      companyId: companyId
    };
  } catch (error) {
    console.error(`❌ Error getting bike rental status for company ${companyId}:`, error);
    return { status: 'Unknown', rental: null, error: error.message, companyId: companyId };
  }
};

// ===== MULTI-TENANT VALIDATION FUNCTIONS =====

/**
 * Validate that rental belongs to company
 * @param {Object} rental - Rental object
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {boolean} - True if rental belongs to company
 */
export const validateCompanyRental = (rental, companyId) => {
  if (!rental || !companyId) {
    return false;
  }
  
  if (rental.companyId !== companyId) {
    console.warn(`🚫 Access denied: Rental ${rental.id} does not belong to company ${companyId} (belongs to ${rental.companyId})`);
    return false;
  }
  
  return true;
};

/**
 * Ensure all data belongs to specific company
 * @param {Array} data - Array of data objects
 * @param {string} companyId - Company ID (REQUIRED)
 * @returns {Array} - Filtered data array
 */
export const ensureCompanyDataIsolation = (data, companyId) => {
  if (!Array.isArray(data)) {
    return [];
  }

  if (!companyId) {
    console.error('❌ ensureCompanyDataIsolation: Company ID required for data isolation');
    return [];
  }
  
  const filtered = data.filter(item => item.companyId === companyId);
  
  if (filtered.length !== data.length) {
    console.warn(`🔒 Filtered ${data.length - filtered.length} items not belonging to company ${companyId}`);
  }
  
  return filtered;
};

/**
 * Validate that all required multi-tenant parameters are provided
 * @param {string} functionName - Name of the calling function
 * @param {string} companyId - Company ID to validate (REQUIRED)
 * @param {Object} currentCompany - Current company context (optional)
 * @returns {boolean} - True if valid
 */
export const validateMultiTenantParams = (functionName, companyId, currentCompany = null) => {
  if (!companyId) {
    console.error(`❌ ${functionName}: Company ID is required for multi-tenant operation`);
    return false;
  }
  
  if (currentCompany && currentCompany.id !== companyId) {
    console.error(`❌ ${functionName}: Company ID mismatch - expected ${currentCompany.id}, got ${companyId}`);
    return false;
  }
  
  return true;
};

/**
 * Ensure rental data belongs to the correct company
 * @param {Object} rental - Rental object
 * @param {string} companyId - Expected company ID (REQUIRED)
 * @returns {boolean} - True if rental belongs to company
 */
export const validateRentalOwnership = (rental, companyId) => {
  if (!rental || !companyId) {
    return false;
  }
  
  if (rental.companyId !== companyId) {
    console.warn(`🚫 Access denied: Rental ${rental.id} belongs to company ${rental.companyId}, not ${companyId}`);
    return false;
  }
  
  return true;
};

// ===== SEARCH AND FILTER FUNCTIONS WITH COMPANY ISOLATION =====

/**
 * Search rentals by customer name or invoice number with company isolation
 * @param {Array} rentals - Array of rental objects
 * @param {string} searchTerm - Search term
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Array} - Filtered company-specific rentals
 */
export const searchRentals = (rentals, searchTerm, companyId) => {
  if (!Array.isArray(rentals)) return [];
  
  if (!companyId) {
    console.error('❌ searchRentals: Company ID required for multi-tenant operation');
    return [];
  }
  
  const companyRentals = rentals.filter(rental => rental.companyId === companyId);
  
  if (!searchTerm) return companyRentals;
  
  const term = searchTerm.toLowerCase().trim();
  
  return companyRentals.filter(rental => {
    const customerName = rental.customerDetails?.name?.toLowerCase() || '';
    const invoiceNumber = rental.invoiceNumber?.toLowerCase() || '';
    const bikeModel = rental.bike?.toLowerCase() || '';
    
    return customerName.includes(term) || 
           invoiceNumber.includes(term) || 
           bikeModel.includes(term);
  });
};

/**
 * Sort rentals by different criteria with company isolation
 * @param {Array} rentals - Array of rental objects
 * @param {string} sortBy - Sort criteria (date, customer, status, bike)
 * @param {string} order - Sort order (asc, desc)
 * @param {string} companyId - Company ID for filtering (REQUIRED)
 * @returns {Array} - Sorted company-specific rentals
 */
export const sortRentals = (rentals, sortBy = 'date', order = 'desc', companyId) => {
  if (!Array.isArray(rentals)) return [];
  
  if (!companyId) {
    console.error('❌ sortRentals: Company ID required for multi-tenant operation');
    return [];
  }
  
  const companyRentals = rentals.filter(rental => rental.companyId === companyId);
  
  const sorted = [...companyRentals].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'customer':
        aValue = a.customerDetails?.name || '';
        bValue = b.customerDetails?.name || '';
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'bike':
        aValue = a.bike || '';
        bValue = b.bike || '';
        break;
      case 'date':
      default:
        aValue = new Date(a.startDate || 0);
        bValue = new Date(b.startDate || 0);
        break;
    }
    
    if (sortBy === 'date') {
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    } else {
      const comparison = aValue.toString().localeCompare(bValue.toString());
      return order === 'asc' ? comparison : -comparison;
    }
  });
  
  return sorted;
};

// ===== EVENT LISTENERS =====

/**
 * Set up event listeners for managed bikes updates (company-aware)
 * @param {Function} onBikeUpdated - Callback when bikes are updated
 * @param {Function} onBikeAdded - Callback when bikes are added
 * @param {Function} onBikeDeleted - Callback when bikes are deleted
 * @param {string} companyId - Company ID for context (REQUIRED)
 */
export const setupManagedBikesEventListeners = (onBikeUpdated, onBikeAdded, onBikeDeleted, companyId) => {
  if (!companyId) {
    console.error('❌ setupManagedBikesEventListeners: Company ID required');
    return () => {}; // Return empty cleanup function
  }

  const handleBikeUpdated = (event) => {
    console.log(`🏍️ Managed bike updated for company ${companyId}:`, event.detail);
    clearCachedManagedBikes(companyId);
    if (onBikeUpdated) onBikeUpdated(event.detail);
  };

  const handleBikeAdded = (event) => {
    console.log(`🏍️ Managed bike added for company ${companyId}:`, event.detail);
    clearCachedManagedBikes(companyId);
    if (onBikeAdded) onBikeAdded(event.detail);
  };

  const handleBikeDeleted = (event) => {
    console.log(`🏍️ Managed bike deleted for company ${companyId}:`, event.detail);
    clearCachedManagedBikes(companyId);
    if (onBikeDeleted) onBikeDeleted(event.detail);
  };

  window.addEventListener('bikeUpdated', handleBikeUpdated);
  window.addEventListener('bikeAdded', handleBikeAdded);
  window.addEventListener('bikeDeleted', handleBikeDeleted);

  return () => {
    window.removeEventListener('bikeUpdated', handleBikeUpdated);
    window.removeEventListener('bikeAdded', handleBikeAdded);
    window.removeEventListener('bikeDeleted', handleBikeDeleted);
  };
};

// ===== VALIDATION FUNCTIONS =====

/**
 * Validate rental dates
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {Object} - Validation result {isValid: boolean, error: string}
 */
export const validateRentalDates = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { isValid: false, error: 'Both start and end dates are required' };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (start < today) {
    return { isValid: false, error: 'Start date cannot be in the past' };
  }

  if (end <= start) {
    return { isValid: false, error: 'End date must be after start date' };
  }

  const maxDuration = 30;
  const duration = calculateRentalDuration(startDate, endDate);
  
  if (duration > maxDuration) {
    return { isValid: false, error: `Rental duration cannot exceed ${maxDuration} days` };
  }

  return { isValid: true, error: null };
};

/**
 * Validate customer details
 * @param {Object} customerDetails - Customer details object
 * @returns {Object} - Validation result {isValid: boolean, errors: Array}
 */
export const validateCustomerDetails = (customerDetails) => {
  const errors = [];

  if (!customerDetails.name || customerDetails.name.trim().length < 2) {
    errors.push('Customer name is required (minimum 2 characters)');
  }

  if (!customerDetails.whatsapp && !customerDetails.localNumber) {
    errors.push('At least one contact number (WhatsApp or local) is required');
  }

  if (customerDetails.email && !isValidEmail(customerDetails.email)) {
    errors.push('Please provide a valid email address');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate email format
 * @param {string} email - Email address
 * @returns {boolean} - True if valid email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// ===== UTILITY HELPER FUNCTIONS =====

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency symbol (default: $)
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = '$') => {
  if (typeof amount !== 'number') return `${currency}0.00`;
  return `${currency}${amount.toFixed(2)}`;
};

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return dateObj.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Generate a random invoice number
 * @param {string} prefix - Prefix for invoice number (default: INV)
 * @returns {string} - Generated invoice number
 */
export const generateLocalInvoiceNumber = (prefix = 'INV') => {
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}${timestamp}${random}`;
};

/**
 * Get status badge color class
 * @param {string} status - Rental status
 * @returns {string} - CSS class string for status badge
 */
export const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Active":
      return "bg-green-900 text-green-400";
    case "Reserved":
      return "bg-indigo-900 text-indigo-400";
    case "Due Today":
      return "bg-amber-900 text-amber-400";
    case "Overdue":
      return "bg-red-900 text-red-400";
    case "Returned":
      return "bg-gray-900 text-gray-400";
    default:
      return "bg-gray-900 text-gray-400";
  }
};

/**
 * Get status border color class
 * @param {string} status - Rental status
 * @returns {string} - CSS class string for status border
 */
export const getStatusBorderClass = (status) => {
  switch (status) {
    case "Active":
      return "border-green-600";
    case "Reserved":
      return "border-indigo-600";
    case "Due Today":
      return "border-amber-600";
    case "Overdue":
      return "border-red-600";
    case "Returned":
      return "border-gray-600";
    default:
      return "border-gray-600";
  }
};

// ===== MIGRATION HELPER =====

/**
 * Helper function to detect if functions have been properly updated for multi-tenancy
 * @param {string} componentName - Name of the component being checked
 * @param {string} companyId - Company ID that should be provided
 */
export const checkMultiTenantMigration = (componentName, companyId) => {
  if (!companyId) {
    console.error(`🚨 MIGRATION ERROR: ${componentName} is not properly configured for multi-tenancy!`);
    console.error(`   Missing company ID parameter. Please update ${componentName} to pass company context.`);
    return false;
  }
  
  console.log(`✅ ${componentName}: Multi-tenant migration complete (Company: ${companyId})`);
  return true;
};

// ===== BACKWARDS COMPATIBILITY FUNCTIONS (DEPRECATED) =====

/**
 * @deprecated Use getTodaysPickups(rentals, companyId) instead
 * Legacy wrapper for backwards compatibility - shows warning
 */
export const getTodaysPickupsLegacy = (rentals) => {
  console.warn('⚠️ getTodaysPickupsLegacy called without company ID - this breaks multi-tenant isolation!');
  console.warn('   Please update to use getTodaysPickups(rentals, companyId) instead');
  
  return rentals ? rentals.filter(rental => {
    if (!rental.startDate) return false;
    if (!isToday(rental.startDate)) return false;
    if (rental.status === 'Returned') return false;
    return true;
  }) : [];
};

/**
 * @deprecated Use getOverdueRentals(rentals, companyId) instead
 * Legacy wrapper for backwards compatibility - shows warning
 */
export const getOverdueRentalsLegacy = (rentals) => {
  console.warn('⚠️ getOverdueRentalsLegacy called without company ID - this breaks multi-tenant isolation!');
  console.warn('   Please update to use getOverdueRentals(rentals, companyId) instead');
  
  return rentals ? rentals.filter(rental => {
    if (!rental.endDate || rental.status === 'Returned') return false;
    const { isOverdue } = checkOverdueStatus(rental);
    return isOverdue;
  }) : [];
};

// ===== EXPORT ALL FUNCTIONS =====

export default {
  // Multi-tenant data loading and caching
  loadRentalsFromFirebase,
  loadManagedBikesFromFirebase,
  updateCachedRentals,
  updateCachedManagedBikes,
  getCachedRentals,
  getCachedManagedBikes,
  clearCachedRentals,
  clearCachedManagedBikes,
  initializeBikeManagement,
  
  // Multi-tenant filtering functions (REQUIRE COMPANY ID)
  getTodaysPickups,
  getTodaysReturns,
  getRentalsDueToday,
  getOverdueRentals,
  getActiveRentals,
  getReservedRentals,
  getCompanyRentalStats,
  
  // Multi-tenant availability functions (REQUIRE COMPANY ID)
  isBikeAvailable,
  isBikeAvailableLocal,
  filterAvailableBikes,
  filterAvailableBikesLocal,
  getBikeRentalStatus,
  
  // Multi-tenant validation functions
  validateCompanyRental,
  ensureCompanyDataIsolation,
  validateMultiTenantParams,
  validateRentalOwnership,
  
  // Multi-tenant search and sort functions (REQUIRE COMPANY ID)
  searchRentals,
  sortRentals,
  
  // Pricing functions (both managed and fallback)
  getModelRate,
  getModelRateFromManaged,
  getModelRateFallback,
  getManagedBikeDetails,
  calculateOverdueCost,
  calculateOverdueCostFromManaged,
  calculateTotalCost,
  calculateTotalCostFromManaged,
  
  // Event listeners
  setupManagedBikesEventListeners,
  
  // Validation functions
  validateRentalDates,
  validateCustomerDetails,
  isValidEmail,
  
  // Utility functions
  formatCurrency,
  formatDate,
  generateLocalInvoiceNumber,
  getStatusBadgeClass,
  getStatusBorderClass,
  
  // Date functions
  isToday,
  isTomorrow,
  calculateRentalDuration,
  getDaysBetween,
  checkOverdueStatus,
  calculateDynamicStatus,
  
  // Migration helpers
  checkMultiTenantMigration,
  
  // Deprecated functions (for backwards compatibility)
  getTodaysPickupsLegacy,
  getOverdueRentalsLegacy
};